// Calcolatore a cascata — uno stadio di lavorazione: intestazione, parametri, costi, righe in uscita e stadi figli annidati
import React, { useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Plus, Save, X, CornerDownRight, Scale, Layers } from "lucide-react";
import { prodApi, errMsg, fmtKg, fmtEur, fmtPct, METHODS, num } from "../prodApi";
import { PBadge } from "../ProdUi";
import { CText, CSelect, IconBtn, toneOf } from "./ui";
import { OutputsHeader, OutputRow } from "./OutputRows";
import { StageParams } from "./StageParams";
import { StageCosts } from "./StageCosts";
import { StagePricing } from "./StagePricing";
import { STAGE_TYPES, stageLabel, stageFromRecipe, templateStage, outputFromNode, pctTotal, stageToRecipeBody, groupOf, splitBalance } from "./model";
import { GroupCreate, GroupBlock } from "./GroupBlock";

const TONE = { cut: "blue", aging: "violet", portion: "green", transform: "amber", external: "slate" };
const fix = (v) => String(+v.toFixed(3)).replace(".", ",");

const MethodToggle = ({ sid, value, onChange }) => (
  <div className="flex flex-wrap bg-slate-900 border border-slate-700 rounded-lg p-0.5" title="Distribuzione del costo sui tagli" data-testid={`casc-stage-method-${sid}`}>
    {METHODS.map((m) => {
      const cls = value === m.value ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white";
      return <button key={m.value} type="button" onClick={() => onChange(m.value)} className={`h-10 px-2.5 rounded-md text-xs font-bold ${cls}`} title={m.hint} data-testid={`casc-stage-method-${sid}-${m.value}`}>{m.label}</button>;
    })}
  </div>
);

const RecipeSelect = ({ sid, stage, recipes, onPick }) => {
  const options = recipes.filter((r) => r.input_node_id === stage.input_node_id);
  return (
    <CSelect value={stage.recipe_id || ""} onChange={(e) => onPick(e.target.value)} className="min-w-[200px] max-w-full" data-testid={`casc-stage-recipe-${sid}`}>
      <option value="">{options.length ? "— Importa ricetta —" : "Nessuna ricetta per questo taglio"}</option>
      {options.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
    </CSelect>
  );
};

const StageSummary = ({ sid, res }) => {
  const t = res.totals;
  const hasChildren = res.rows.some((r) => r.child);
  return (
    <p className="text-xs text-slate-400 tabular-nums flex flex-wrap gap-x-3 gap-y-1" data-testid={`casc-stage-summary-${sid}`}>
      <span><Scale size={12} className="inline mr-1" />In: <b className="text-white">{fmtKg(res.origin.total_kg, 1)} kg</b> a {fmtEur(res.input.price_per_kg_effective)}/kg = {fmtEur(res.input.input_cost)}{res.depth > 0 && <span className="text-slate-500"> (costo industriale a monte)</span>}</span>
      <span>Lavorazione <b className="text-white">{fmtEur(res.costs.processing)}</b></span>
      <span>Ricavi{hasChildren ? " finali" : ""} <b className="text-white">{fmtEur(t.revenue_total)}</b></span>
      <span className={toneOf(t.margin)}>Margine <b>{fmtEur(t.margin)}</b> ({fmtPct(t.margin_pct)})</span>
      {t.stages > 1 && <span className={toneOf(t.margin_delta_processing)}>Δ lavorazioni successive {t.margin_delta_processing >= 0 ? "+" : ""}{fmtEur(t.margin_delta_processing)}</span>}
      <span>Resa vendibile {fmtPct(res.mass.sellable_yield_pct)}</span>
      {!res.mass.reconciled && <span className="text-amber-300">Non riconciliato {fmtKg(res.mass.unreconciled_kg, 2)} kg</span>}
    </p>
  );
};

const RecipeButtons = ({ sid, stage, isManager, balanced, saving, onSave }) => {
  if (!isManager) return null;
  if (stage.recipe_id) {
    return <IconBtn tone="green" title="Scrive rese, prezzi e costi di questo stadio nella ricetta base (nuova versione)" disabled={saving || !balanced} onClick={() => onSave(false)} data-testid={`casc-stage-recipe-update-${sid}`}><Save size={14} />Aggiorna ricetta</IconBtn>;
  }
  return <IconBtn tone="green" title="Salva questo stadio come nuova ricetta" disabled={saving || !balanced || !stage.outputs.length} onClick={() => onSave(true)} data-testid={`casc-stage-recipe-create-${sid}`}><Save size={14} />Salva come ricetta</IconBtn>;
};

const ChildBlock = ({ o, rr, sid, depth, common, update }) => {
  const label = STAGE_TYPES.find((t) => t.value === o.child.type)?.label;
  const childUpdate = (fn) => update((s) => ({ ...s, outputs: s.outputs.map((x) => (x.key === o.key ? { ...x, child: fn(x.child) } : x)) }));
  const childRemove = () => update((s) => ({ ...s, outputs: s.outputs.map((x) => (x.key === o.key ? { ...x, child: null } : x)) }));
  const ct = rr?.child?.totals;
  const cost = rr ? (rr.transfer_cost_per_kg ?? rr.cost_per_kg) : null;
  return (
    <div className="ml-1 md:ml-6 mr-0 md:mr-2 my-2 pl-1 md:pl-3 border-l-2 border-blue-500/40" data-testid={`casc-child-${sid}-${o.key}`}>
      <div className="py-2 space-y-0.5">
        <p className="text-xs text-blue-300 font-bold flex items-center gap-1"><CornerDownRight size={13} />{o.node_name} → {label}</p>
        {rr && <p className="text-[11px] text-slate-400 tabular-nums" data-testid={`casc-child-input-${sid}-${o.key}`}>Ingresso <b className="text-white">{fmtKg(rr.kg, 2)} kg</b> a <b className="text-white">{fmtEur(cost)}/kg</b> = {fmtEur(rr.kg * cost)} · {rr.transferred ? "valore del recupero trasferito" : <>costo industriale calcolato a monte, <span className="text-amber-200">non il prezzo di vendita {fmtEur(rr.sale_price_per_kg)}</span></>}</p>}
        {ct && <p className="text-[11px] tabular-nums" data-testid={`casc-child-margin-${sid}-${o.key}`}>Margine effettivo su tutto il {o.node_name} lavorato: <b className={toneOf(ct.margin)}>{fmtEur(ct.margin)}</b> ({fmtPct(ct.margin_pct)} del ricavo{ct.markup_pct != null ? `, ricarico ${fmtPct(ct.markup_pct)}` : ""}) · ricavo finale {fmtEur(ct.revenue_total)} · {rr.transferred ? "recupero diretto" : `vendendolo subito ${fmtEur(rr.margin)}`} → <b className={toneOf(rr.margin_delta_processing)}>{rr.margin_delta_processing >= 0 ? "+" : ""}{fmtEur(rr.margin_delta_processing)}</b></p>}
      </div>
      {/* Ricorsione via createElement: la strumentazione visuale di sviluppo va in loop sui componenti JSX ricorsivi */}
      {React.createElement(StageCard, { stage: o.child, depth: depth + 1, update: childUpdate, remove: childRemove, ...common })}
    </div>
  );
};

export const StageCard = ({ stage, results, nodes, recipes, isManager, update, remove, depth = 0, onRecipeSaved, globalTarget = 25 }) => {
  const res = results[stage.id];
  const [showCosts, setShowCosts] = useState(false);
  const [showPricing, setShowPricing] = useState(depth === 0);
  const [groupOpen, setGroupOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const total = pctTotal(stage);
  const balanced = Math.abs(total - 100) < 0.005;
  const inputKg = res?.input.total_kg || null;
  const sid = stage.id;
  const common = { results, nodes, recipes, isManager, onRecipeSaved, globalTarget };

  const importRecipe = async (rid) => {
    if (!rid) { update((s) => ({ ...s, recipe_id: null, recipe_name: null })); return; }
    if (stage.outputs.some((o) => o.child) && !window.confirm("Cambiando ricetta si perdono le lavorazioni già aggiunte sotto i tagli di questo stadio. Continuare?")) return;
    try {
      const r = await prodApi.get(`/production/recipes/${rid}`);
      update((s) => ({ ...stageFromRecipe(r.data, s.type), id: s.id, method: s.method }));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveRecipe = async (asNew) => {
    const name = asNew ? window.prompt("Nome della nuova ricetta", stage.name) : stage.recipe_name;
    if (asNew && !name) return;
    if (!asNew && !window.confirm(`Aggiornare la ricetta «${stage.recipe_name}» con rese, prezzi e costi di questo stadio? Vale per tutti (nuova versione).`)) return;
    setSaving(true);
    try {
      const body = stageToRecipeBody(stage, asNew ? name : stage.recipe_name);
      const r = asNew ? await prodApi.post("/production/recipes", body) : await prodApi.put(`/production/recipes/${stage.recipe_id}`, body);
      update((s) => ({ ...s, recipe_id: r.data.id, recipe_name: r.data.name, recipe_version: r.data.version }));
      toast.success(asNew ? `Ricetta «${r.data.name}» creata` : `Ricetta aggiornata alla versione ${r.data.version}`);
      if (onRecipeSaved) onRecipeSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const balance = () => {
    const idx = [...stage.outputs].reverse().findIndex((o) => o.disposal === "waste");
    if (idx < 0) { toast.error("Aggiungi una riga di scarto o calo su cui scaricare la differenza"); return; }
    const i = stage.outputs.length - 1 - idx;
    update((s) => ({ ...s, outputs: s.outputs.map((o, j) => (j === i ? { ...o, pct: fix(num(o.pct) + 100 - total) } : o)) }));
  };
  const addRow = () => {
    if (stage.type === "split") { const base = stage.outputs[0]; if (base) update((s) => ({ ...s, outputs: [...s.outputs, { ...base, key: Math.random().toString(36).slice(2, 10), node_name: `${base.node_name.split(" · ")[0]} · altra destinazione`, pct: "0", child: null }] })); return; }
    const n = nodes.find((x) => x.kind === "meat") || nodes[0];
    if (n) update((s) => ({ ...s, outputs: [...s.outputs, outputFromNode(n)] }));
  };
  const rowUpdate = (key, patch) => update((s) => {
    const outputs = s.outputs.map((o) => (o.key === key ? { ...o, ...patch } : o));
    return { ...s, outputs: s.type === "split" && patch.pct != null ? splitBalance(outputs, key) : outputs };
  });
  const rowRemove = (key) => update((s) => ({ ...s, outputs: s.outputs.filter((x) => x.key !== key) }));
  const setStage = (s) => update(() => s);
  const frame = depth ? "border-slate-700 bg-slate-800/40" : "border-slate-600 bg-slate-800/60";
  const costsLabel = res ? <span className="text-slate-400">{fmtEur(res.costs.processing)}</span> : null;

  return (
    <div className={`rounded-2xl border ${frame}`} data-testid={`casc-stage-${sid}`} data-depth={depth}>
      <div className="p-3 md:p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <PBadge tone={TONE[stage.type] || "slate"} data-testid={`casc-stage-type-${sid}`}>{stageLabel(stage.type)}</PBadge>
          <CText value={stage.name} onChange={(e) => update((s) => ({ ...s, name: e.target.value }))} className="flex-1 min-w-[160px] font-bold" data-testid={`casc-stage-name-${sid}`} />
          {["cut", "transform"].includes(stage.type) && <RecipeSelect sid={sid} stage={stage} recipes={recipes} onPick={importRecipe} />}
          <MethodToggle sid={sid} value={stage.method} onChange={(m) => update((s) => ({ ...s, method: m }))} />
          <IconBtn title="Costi di lavorazione" onClick={() => setShowCosts(!showCosts)} data-testid={`casc-stage-costs-toggle-${sid}`}>{showCosts ? <ChevronUp size={14} /> : <ChevronDown size={14} />}Costi {costsLabel}</IconBtn>
          <IconBtn title="Prezzi: margine obiettivo, prezzi proposti, rese storiche" onClick={() => setShowPricing(!showPricing)} data-testid={`casc-stage-pricing-toggle-${sid}`}>{showPricing ? <ChevronUp size={14} /> : <ChevronDown size={14} />}Prezzi{res?.warnings?.length ? <span className="ml-1 w-2 h-2 rounded-full bg-amber-400" /> : null}</IconBtn>
          <RecipeButtons sid={sid} stage={stage} isManager={isManager} balanced={balanced} saving={saving} onSave={saveRecipe} />
          {remove && <IconBtn tone="red" title="Togli questa lavorazione" onClick={remove} data-testid={`casc-stage-remove-${sid}`}><X size={14} /></IconBtn>}
        </div>
        {res && <StageSummary sid={sid} res={res} />}
        <StageParams stage={stage} res={res} onChange={setStage} depth={depth} />
        {showPricing && <StagePricing stage={stage} res={res} globalTarget={globalTarget} onChange={setStage} />}
        {showCosts && <StageCosts stage={stage} res={res} onChange={setStage} />}
      </div>
      <div className="border-t border-slate-700/60 pb-2" data-testid={`casc-outputs-${sid}`}>
        <OutputsHeader />
        {stage.outputs.map((o) => (
          <React.Fragment key={o.key}>
            <OutputRow row={o} res={res?.rows.find((r) => r.out_key === o.key)} inputKg={inputKg} nodes={nodes} stageId={sid} method={stage.method} isManager={isManager}
              grouped={groupOf(stage, o.key)?.name} stageType={stage.type} stagePieces={res?.input.pieces}
              onChange={(patch) => rowUpdate(o.key, patch)} onRemove={() => rowRemove(o.key)} onAddChild={(type) => rowUpdate(o.key, { child: templateStage(type, o, nodes) })} />
            {o.child && <ChildBlock o={o} rr={res?.rows.find((r) => r.out_key === o.key)} sid={sid} depth={depth} common={common} update={update} />}
          </React.Fragment>
        ))}
        {(stage.groups || []).map((g) => <GroupBlock key={g.id} g={g} gr={res?.groups?.find((x) => x.id === g.id)} stage={stage} sid={sid} depth={depth} common={common} update={update} StageCard={StageCard} />)}
        {groupOpen && <GroupCreate stage={stage} nodes={nodes} sid={sid} onClose={() => setGroupOpen(false)} onCreate={(g) => update((s) => ({ ...s, groups: [...(s.groups || []), g] }))} />}
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 pt-2">
          <div className="flex items-center gap-2">
            <PBadge tone={balanced ? "green" : "amber"} data-testid={`casc-stage-pct-${sid}`}>Σ {fmtPct(total, 2)}</PBadge>
            {!balanced && stage.outputs.length > 0 && <IconBtn title="Porta la somma a 100% scaricando la differenza sull'ultima riga di scarto/calo" onClick={balance} data-testid={`casc-stage-balance-${sid}`}>Bilancia a 100%</IconBtn>}
          </div>
          <div className="flex items-center gap-2">
            {stage.type !== "split" && stage.outputs.length > 1 && <IconBtn title="Più tagli insieme in una stessa lavorazione (es. tartare, c/lavorazione salumi)" onClick={() => setGroupOpen(!groupOpen)} data-testid={`casc-stage-group-btn-${sid}`}><Layers size={14} />Raggruppa tagli</IconBtn>}
            <IconBtn tone="blue" title={stage.type === "split" ? "Aggiungi una destinazione" : "Aggiungi un taglio in uscita"} onClick={addRow} data-testid={`casc-stage-add-row-${sid}`}><Plus size={14} />{stage.type === "split" ? "Destinazione" : "Taglio"}</IconBtn>
          </div>
        </div>
      </div>
    </div>
  );
};
